import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Smartphone, Star } from "lucide-react";
import { Button } from "@heroui/react";
import {
  addToHomeScreen,
  canAddToHomeScreen,
  clearInstallPromptEvent,
  isInstalled,
  setInstallPromptEvent,
} from "@/utils/pwa";

export const InstallPrompt = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [showPrompt, setShowPrompt] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const isIos =
    typeof window !== "undefined" &&
    /iPad|iPhone|iPod/.test(window.navigator.userAgent);

  useEffect(() => {
    const dismissed = localStorage.getItem("install-prompt-dismissed-until");

    if (dismissed) {
      const dismissedUntil = Number(dismissed);
      if (dismissedUntil > Date.now()) {
        setIsDismissed(true);
        return;
      }
      localStorage.removeItem("install-prompt-dismissed-until");
    }

    if (isInstalled()) {
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPromptEvent(e);

      setTimeout(() => {
        setShowPrompt(true);
      }, 2500);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    if (isIos) {
      const timer = setTimeout(() => {
        if (!isDismissed) {
          setShowPrompt(true);
        }
      }, 3000);

      return () => {
        clearTimeout(timer);
        window.removeEventListener(
          "beforeinstallprompt",
          handleBeforeInstallPrompt,
        );
      };
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, [isDismissed, isIos]);

  const handleInstall = async () => {
    if (isIos) {
      setShowPrompt(true);
      return;
    }

    const installed = await addToHomeScreen();

    if (installed) {
      clearInstallPromptEvent();
      setShowPrompt(false);

      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(t("INSTALL_PROMPT.NOTIFICATION_TITLE"), {
          body: t("INSTALL_PROMPT.NOTIFICATION_BODY"),
          icon: "/images/logo.png",
          badge: "/images/logo.png",
        });
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setIsDismissed(true);
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
    localStorage.setItem(
      "install-prompt-dismissed-until",
      String(Date.now() + thirtyDaysInMs),
    );
  };

  const handleLater = () => {
    setShowPrompt(false);
    const oneDayInMs = 24 * 60 * 60 * 1000;
    localStorage.setItem(
      "install-prompt-dismissed-until",
      String(Date.now() + oneDayInMs),
    );
  };

  if (!showPrompt || isInstalled() || (!isIos && !canAddToHomeScreen()) || window.location.pathname === "/full-menu") {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96"
      >
        <div className="bg-linear-to-r from-primary to-primary/90 text-[#EAE4DB] rounded-2xl shadow-2xl border border-primary/30 backdrop-blur-xl p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Smartphone className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold">
                  {t("INSTALL_PROMPT.TITLE")}
                </h3>
                <p className="text-sm text-[#EAE4DB]/80">
                  {isIos
                    ? t("INSTALL_PROMPT.SUBTITLE_IOS")
                    : t("INSTALL_PROMPT.SUBTITLE")}
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Benefits */}
          <div className="space-y-2 mb-6">
            {isIos && (
              <div className="rounded-2xl bg-white/15 px-4 py-3 text-sm leading-relaxed">
                {t("INSTALL_PROMPT.IOS_STEPS")}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Star className="w-4 h-4 text-yellow-300" />
              <span>{t("INSTALL_PROMPT.BENEFIT_FAST")}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Star className="w-4 h-4 text-yellow-300" />
              <span>{t("INSTALL_PROMPT.BENEFIT_OFFLINE")}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Star className="w-4 h-4 text-yellow-300" />
              <span>{t("INSTALL_PROMPT.BENEFIT_PUSH")}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              color="secondary"
              variant="flat"
              className="flex-1 rounded-full bg-white/20 text-[#EAE4DB] border-white/30 hover:bg-white/30"
              onPress={handleLater}
            >
              {t("INSTALL_PROMPT.LATER")}
            </Button>
            <Button
              className="flex-1 rounded-full bg-white text-primary font-bold"
              onPress={handleInstall}
              startContent={<Download className="w-4 h-4" />}
            >
              {isIos
                ? t("INSTALL_PROMPT.SHOW_STEPS")
                : t("INSTALL_PROMPT.INSTALL")}
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
